// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import { Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '../ui/label';
import { EducBox, ChartBox, ResultCard, InputRow, PremiumLock, StrumentoSelect, RiskWarning, EDUC, STRUMENTI_MAP, baseChartOpts, fmt } from './shared';

const PACComparisonSection = ({ monthly, anni }) => {
  const [str1, setStr1] = useState('etf_world');
  const [str2, setStr2] = useState('sp500');
  const [str3, setStr3] = useState('bitcoin');

  const selected = [str1, str2, str3].filter(Boolean);
  const enough = selected.length >= 2;
  const colors = ['#3D7A52', '#C9A84C', '#85B7EB'];

  const calcSeries = (strId) => {
    const s = STRUMENTI_MAP[strId];
    if (!s) return [];
    const rM = s.ret / 100 / 12;
    return Array.from({ length: anni + 1 }, (_, i) => {
      const ni = i * 12;
      return Math.round(rM > 0 ? monthly * ((Math.pow(1 + rM, ni) - 1) / rM) : monthly * ni);
    });
  };

  const labels = Array.from({ length: anni + 1 }, (_, i) => i % 2 === 0 ? `Anno ${i}` : '');
  const datasets = selected.map((id, ci) => ({
    label: STRUMENTI_MAP[id]?.label || id,
    data: calcSeries(id),
    borderColor: colors[ci],
    backgroundColor: `${colors[ci]}18`,
    fill: true, tension: 0.4, pointRadius: 0,
  }));

  const tableData = selected.map((id) => {
    const series = calcSeries(id);
    const finale = series[series.length - 1] || 0;
    const investito = monthly * 12 * anni;
    const guadagno = finale - investito;
    return { id, name: STRUMENTI_MAP[id]?.label || id, finale, guadagno, mult: investito > 0 ? (finale / investito).toFixed(2) : '—' };
  });
  const best = tableData.reduce((b, t) => (!b || t.finale > b.finale ? t : b), null);

  return (
    <div className="relative mt-8" style={{ position: 'relative' }}>
      <div className="sapiens-card p-6">
        <h3 className="text-base font-semibold text-[#E8E8E4] mb-1">Confronta più strumenti</h3>
        <p className="text-sm text-[#888780] mb-5">Stesso importo e durata, strumenti diversi a confronto</p>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          {[str1, str2, str3].map((v, i) => (
            <div key={i}>
              <Label className="text-[#888780] text-xs mb-1">Strumento {i + 1}</Label>
              <StrumentoSelect value={v} onChange={[setStr1, setStr2, setStr3][i]} testId={`compare-str-${i + 1}`} placeholder={i === 2 ? '— Opzionale —' : undefined} />
            </div>
          ))}
        </div>
        {!enough ? (
          <p className="text-center text-[#888780] text-sm py-6">Seleziona almeno 2 strumenti per confrontare</p>
        ) : (
          <>
            <ChartBox height={440}>
              <Line
                data={{ labels, datasets }}
                options={{ ...baseChartOpts(), plugins: { ...baseChartOpts().plugins, legend: { display: true, labels: { color: '#888780', usePointStyle: true } }, tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: €${fmt(ctx.parsed.y)}` } } } }}
              />
            </ChartBox>
            <div className="mt-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[#888780] border-b border-white/[0.06]">
                    <th className="text-left py-2">Strumento</th>
                    <th className="text-right py-2">Capitale finale</th>
                    <th className="text-right py-2">Guadagno netto</th>
                    <th className="text-right py-2">Moltiplicatore</th>
                    <th className="text-right py-2">vs migliore</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row) => (
                    <tr key={row.id} style={{ background: row.id === best?.id ? 'rgba(61,122,82,0.08)' : 'transparent' }} className="border-b border-white/[0.04]">
                      <td className={`py-2 pr-2 font-medium ${row.id === best?.id ? 'text-[#3D7A52]' : 'text-[#E8E8E4]'}`}>{row.name}</td>
                      <td className="text-right py-2 text-[#E8E8E4]">€{fmt(row.finale)}</td>
                      <td className="text-right py-2 text-[#3D7A52]">€{fmt(row.guadagno)}</td>
                      <td className="text-right py-2 text-[#C9A84C]">{row.mult}×</td>
                      <td className="text-right py-2 text-[#888780]">{row.id === best?.id ? '—' : `−€${fmt(best.finale - row.finale)}`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      <PremiumLock />
    </div>
  );
};

const PACAvanzatoCalc = () => {
  const [monthly, setMonthly] = useState('300');
  const [anni, setAnni] = useState('20');
  const [strumento, setStrumento] = useState('etf_world');

  const s = STRUMENTI_MAP[strumento] || STRUMENTI_MAP.etf_world;
  const m = parseFloat(monthly) || 0;
  const n = parseInt(anni) || 0;
  const rM = s.ret / 100 / 12;
  const invested = m * 12 * n;
  const final = rM > 0 ? m * ((Math.pow(1 + rM, n * 12) - 1) / rM) : invested;

  const labels = Array.from({ length: n + 1 }, (_, i) => i % 2 === 0 ? `Anno ${i}` : '');
  const dataLine = Array.from({ length: n + 1 }, (_, i) => {
    const ni = i * 12;
    return Math.round(rM > 0 ? m * ((Math.pow(1 + rM, ni) - 1) / rM) : m * ni);
  });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ position: 'relative' }}>
      <EducBox text={EDUC.pacAvanzato} />
      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <InputRow label="Versamento mensile (€)" value={monthly} onChange={setMonthly} prefix="€" />
        <InputRow label="Durata (anni)" value={anni} onChange={setAnni} suffix="anni" />
        <div>
          <Label className="text-[#888780] text-sm mb-1">Strumento di investimento</Label>
          <StrumentoSelect value={strumento} onChange={setStrumento} testId="pac-adv-select" />
          {s.tooltip && <p className="text-xs text-[#888780] mt-1 italic">{s.tooltip}</p>}
          <RiskWarning strumentoId={strumento} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <ResultCard label="Capitale finale" value={`€${fmt(final)}`} green />
        <ResultCard label="Versato" value={`€${fmt(invested)}`} />
        <ResultCard label="Guadagno netto" value={`€${fmt(final - invested)}`} gold />
      </div>
      <ChartBox title={`Crescita con ${s.label}`} height={420}>
        <Line
          data={{ labels, datasets: [{ data: dataLine, borderColor: '#3D7A52', backgroundColor: 'rgba(61,122,82,0.1)', fill: true, tension: 0.4, pointRadius: 0 }] }}
          options={baseChartOpts()}
        />
      </ChartBox>
      <div className="flex justify-end mt-3">
        <button
          data-testid="share-pac-avanzato-btn"
          onClick={() => {
            const text = `Con €${m}/mese per ${n} anni su ${s.label} raggiungo €${fmt(final)}! Scopri il potere dell'interesse composto su SAPIENS 📈 finsapiens.it`;
            if (navigator.share) navigator.share({ text });
            else { navigator.clipboard.writeText(text); toast.success('Testo copiato!'); }
          }}
          className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-xl text-[#888780] hover:text-[#E8E8E4] transition-colors"
          style={{ border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <Share2 className="w-3.5 h-3.5" />Condividi risultato
        </button>
      </div>
      <div style={{ position: 'absolute', inset: 0 }}>
        <PremiumLock />
      </div>
      <PACComparisonSection monthly={m} anni={n} />
</motion.div>
  );
};

export default PACAvanzatoCalc;
